// API Configuration
export const API_CONFIG = {
  // API base URL. Default is EMPTY = same-origin relative requests (e.g. "/api/v1/...").
  // The frontend's nginx (frontend/nginx.conf) proxies "/api/" to the gateway on :8099,
  // so the browser only ever talks to the origin that served the page. This avoids both
  // CORS and Chrome's Private Network Access block (a public-IP page may not call
  // localhost/loopback). Override REACT_APP_API_URL only for non-proxied local dev
  // (e.g. "http://localhost:8099" when running CRA directly without the nginx container).
  BASE_URL: process.env.REACT_APP_API_URL || '',
  TIMEOUT: 30000,
  RETRY_COUNT: 3,
  RETRY_DELAY: 1000,
};

// API Endpoints
export const ENDPOINTS = {
  // CSPM Overview
  CSPM_OVERVIEW: '/api/v1/cspm/overview',

  // Inventory
  INVENTORY_RESOURCES: '/api/v1/inventory/resources',
  INVENTORY_RESOURCE: (id: string) => `/api/v1/inventory/resources/${id}`,
  INVENTORY_SUMMARY: '/api/v1/inventory/resources/summary',
  INVENTORY_STATISTICS: '/api/v1/inventory/resources/statistics',
  INVENTORY_TYPES: '/api/v1/inventory/resources/types',
  INVENTORY_GRAPH: '/api/v1/inventory/resources/graph',
  INVENTORY_EXPORT: '/api/v1/inventory/resources/export',

  // Misconfigurations / Findings
  CSPM_FINDINGS: '/api/v1/cspm/findings',
  CSPM_FINDING: (id: string) => `/api/v1/cspm/findings/${id}`,
  CSPM_FINDING_STATUS: (id: string) => `/api/v1/cspm/findings/${id}/status`,
  CSPM_FINDINGS_STATISTICS: '/api/v1/cspm/findings/statistics',
  CSPM_FINDINGS_CATEGORIES: '/api/v1/cspm/findings/categories',
  CSPM_FINDINGS_ATTACK_PATHS: '/api/v1/cspm/findings/attack-paths',
  CSPM_FINDINGS_RISK_SCORE: '/api/v1/cspm/findings/risk-score',
  CSPM_FINDING_SUPPRESS: (id: string) => `/api/v1/cspm/findings/${id}/suppress`,
  CSPM_FINDING_REMEDIATE: (id: string) => `/api/v1/cspm/findings/${id}/remediate`,
  CSPM_FINDINGS_EXPORT: '/api/v1/cspm/findings/export',

  // Posture Issues (Wiz-style finding aggregation)
  CSPM_ISSUES: '/api/v1/cspm/findings/issues',
  CSPM_ISSUE: (id: string) => `/api/v1/cspm/findings/issues/${id}`,
  CSPM_ISSUES_STATISTICS: '/api/v1/cspm/findings/issues/statistics',

  // Policies
  CSPM_POLICIES: '/api/v1/cspm/policies',
  CSPM_POLICY: (id: string) => `/api/v1/cspm/policies/${id}`,

  // Compliance — Sprint 1 / Phase 1C: switched from /api/v1/cspm/compliance/* to /api/v1/compliance/*.
  // The cspm-compliance-service was retired (it served random-mock in-memory data, not real DB
  // state, in violation of the "no mock data" rule). compliance-hub at port 8055 is now the
  // single canonical compliance service for both the main Compliance page and the CSPM
  // dashboard's Compliance sub-tab. sections + controls DO return 200 (rows are
  // BE-truncated to ~4 per framework, not missing). Endpoints that don't exist in hub
  // (enable, controls/{id}/resources, benchmark, export/controls) will 404 — Compliance.tsx
  // already gracefully handles missing data via loading/empty-state branches.
  CSPM_COMPLIANCE_SUMMARY: '/api/v1/compliance/stats',
  CSPM_COMPLIANCE_FRAMEWORKS: '/api/v1/compliance/frameworks',
  CSPM_COMPLIANCE_FRAMEWORK: (id: string) => `/api/v1/compliance/frameworks/${id}`,
  CSPM_COMPLIANCE_FRAMEWORK_SECTIONS: (id: string) => `/api/v1/compliance/frameworks/${id}/sections`,
  CSPM_COMPLIANCE_FRAMEWORK_CONTROLS: (id: string) => `/api/v1/compliance/frameworks/${id}/controls`,
  CSPM_COMPLIANCE_FRAMEWORK_ENABLE: (id: string) => `/api/v1/compliance/frameworks/${id}/toggle`,
  CSPM_COMPLIANCE_CONTROL: (id: string) => `/api/v1/compliance/controls/${id}`,
  CSPM_COMPLIANCE_CONTROL_RESOURCES: (id: string) => `/api/v1/compliance/controls/${id}/resources`,
  CSPM_COMPLIANCE_ASSESSMENTS: '/api/v1/compliance/assessments',
  CSPM_COMPLIANCE_ASSESSMENT: (id: string) => `/api/v1/compliance/assessments/${id}`,
  // /trends/weekly returns a degenerate single point and takes `weeks`, not `days`;
  // /trends is the real time series ({ current_score, trends:[{date,score}], ... }).
  CSPM_COMPLIANCE_TRENDS: '/api/v1/compliance/trends',
  CSPM_COMPLIANCE_BENCHMARK: '/api/v1/compliance/benchmark',
  // Real compliance-hub export routes (verified present):
  //   GET /api/v1/compliance/export/{framework}             -> JSON / CSV / OSCAL payload
  //   GET /api/v1/compliance/reports/{framework}/export.csv  -> real CSV file blob (DB-backed)
  // The previous flat paths (/compliance/export and /compliance/export/controls)
  // had no BE handler and 404'd, so they are replaced by these framework-scoped ones.
  CSPM_COMPLIANCE_EXPORT_FW: (framework: string) => `/api/v1/compliance/export/${encodeURIComponent(framework)}`,
  CSPM_COMPLIANCE_EXPORT_CSV: (framework: string) => `/api/v1/compliance/reports/${encodeURIComponent(framework)}/export.csv`,

  // Risk
  CSPM_RISK_SUMMARY: '/api/v1/cspm/risk/summary',

  // Risk Graph
  CSPM_RISK_GRAPH: '/api/v1/cspm/risk/graph',
  CSPM_RISK_GRAPH_BUILD: '/api/v1/cspm/risk/graph/build',
  CSPM_RISK_ATTACK_PATHS: '/api/v1/cspm/risk/paths',
  CSPM_RISK_BLAST_RADIUS: (resourceId: string) => `/api/v1/cspm/risk/blast-radius/${resourceId}`,
  CSPM_RISK_ENTRY_POINTS: '/api/v1/cspm/risk/entry-points',
  // R140: /api/v1/cspm/risk/toxic-combinations is served by nothing (CSPM 8013
  // has no toxic route and no rewrite maps it) — it 404'd on every /cspm load.
  // Toxic combinations live on graph-engine; kept as an alias so any remaining
  // reference resolves to the route that actually answers.
  CSPM_RISK_TOXIC_COMBINATIONS: '/api/v1/toxic-combinations',
  TOXIC_COMBINATIONS: '/api/v1/toxic-combinations',
  CSPM_RISK_TOP_RISKS: '/api/v1/cspm/risk/top-risks',
  CSPM_RISK_ATTACK_SURFACE: '/api/v1/cspm/risk/attack-surface',
  CSPM_RISK_EXPOSURE_MAP: '/api/v1/cspm/risk/exposure-map',

  // Remediation
  CSPM_REMEDIATION: (policyId: string) => `/api/v1/cspm/remediation/${policyId}`,
  CSPM_REMEDIATION_TASKS: '/api/v1/cspm/remediation/tasks',
  CSPM_REMEDIATION_TASK: (taskId: string) => `/api/v1/cspm/remediation/tasks/${taskId}`,
  CSPM_REMEDIATION_EXECUTE: (taskId: string) => `/api/v1/cspm/remediation/tasks/${taskId}/execute`,
  CSPM_REMEDIATION_APPROVE: (taskId: string) => `/api/v1/cspm/remediation/tasks/${taskId}/approve`,
  CSPM_REMEDIATION_ROLLBACK: (taskId: string) => `/api/v1/cspm/remediation/tasks/${taskId}/rollback`,
  CSPM_REMEDIATION_CANCEL: (taskId: string) => `/api/v1/cspm/remediation/tasks/${taskId}/cancel`,
  CSPM_REMEDIATION_RETRY: (taskId: string) => `/api/v1/cspm/remediation/tasks/${taskId}/retry`,
  CSPM_REMEDIATION_ASSIGN: (taskId: string) => `/api/v1/cspm/remediation/tasks/${taskId}/assign`,
  CSPM_REMEDIATION_BULK: '/api/v1/cspm/remediation/bulk',
  CSPM_REMEDIATION_AUTO: '/api/v1/cspm/remediation/auto-remediate',
  CSPM_REMEDIATION_STATS: '/api/v1/cspm/remediation/stats',
  CSPM_REMEDIATION_TIMELINE: '/api/v1/cspm/remediation/timeline',
  CSPM_REMEDIATION_FOR_FINDING: (findingId: string) => `/api/v1/cspm/remediation/for-finding/${findingId}`,
  CSPM_REMEDIATION_PLAYBOOKS: '/api/v1/cspm/remediation/playbooks',
  CSPM_REMEDIATION_PLAYBOOK: (playbookId: string) => `/api/v1/cspm/remediation/playbooks/${playbookId}`,
  CSPM_REMEDIATION_PLAYBOOK_CATEGORIES: '/api/v1/cspm/remediation/playbooks/categories',

  // Reports
  CSPM_REPORTS_SUMMARY: '/api/v1/cspm/reports/summary',
  CSPM_REPORTS: '/api/v1/cspm/reports',
  CSPM_REPORT: (id: string) => `/api/v1/cspm/reports/${id}`,
  CSPM_REPORT_GENERATE: (id: string) => `/api/v1/cspm/reports/${id}/generate`,
  CSPM_REPORT_TOGGLE: (id: string) => `/api/v1/cspm/reports/${id}/toggle`,
  CSPM_REPORTS_TEMPLATES: '/api/v1/cspm/reports/templates',
  CSPM_REPORTS_GENERATED: '/api/v1/cspm/reports/generated',

  // Scans
  CSPM_SCAN_TRIGGER: '/api/v1/cspm/scan',
  CSPM_SCANS: '/api/v1/cspm/scans',

  // Settings
  CSPM_SETTINGS: '/api/v1/cspm/settings',

  // Cloud Accounts
  CSPM_CLOUD_ACCOUNTS: '/api/v1/cspm/accounts',

  // Notification Channels
  CSPM_NOTIFICATION_CHANNELS: '/api/v1/cspm/notification-channels',

  // Scan Schedules
  CSPM_SCAN_SCHEDULES: '/api/v1/cspm/scan-schedules',

  // Integrations
  CSPM_INTEGRATIONS: '/api/v1/cspm/integrations',
  CSPM_INTEGRATION_CONNECT: (id: string) => `/api/v1/cspm/integrations/${id}/connect`,
  CSPM_INTEGRATION_DISCONNECT: (id: string) => `/api/v1/cspm/integrations/${id}/disconnect`,
  CSPM_INTEGRATION_TEST: (id: string) => `/api/v1/cspm/integrations/${id}/test`,

  // API Keys
  CSPM_API_KEYS: '/api/v1/cspm/api-keys',

  // Control Plane
  CSPM_CONTROL_PLANE_STATUS: '/api/v1/cspm/control-plane/status',
  CSPM_CONTROL_PLANE_ARCHITECTURE: '/api/v1/cspm/control-plane/architecture',
  CSPM_CONTROL_PLANE_SYNC: '/api/v1/cspm/settings/control-plane/sync',

  // Cloud Onboarding
  CSPM_ONBOARDING: (provider: string) => `/api/v1/cspm/onboarding/${provider}`,
  CSPM_ONBOARDING_GENERATE_TEMPLATE: (provider: string) => `/api/v1/cspm/onboarding/${provider}/generate-template`,

  // General Settings
  CSPM_SETTINGS_GENERAL: '/api/v1/cspm/settings/general',
  CSPM_SETTINGS_GENERAL_AUTO_REMEDIATION: '/api/v1/cspm/settings/general/auto-remediation',
  CSPM_SETTINGS_GENERAL_RISK_THRESHOLD: '/api/v1/cspm/settings/general/risk-threshold',
  CSPM_SETTINGS_GENERAL_DATA_RETENTION: '/api/v1/cspm/settings/general/data-retention',
  CSPM_SETTINGS_GENERAL_TIMEZONE: '/api/v1/cspm/settings/general/timezone',
  CSPM_SETTINGS_GENERAL_SEVERITY_FILTER: '/api/v1/cspm/settings/general/severity-filter',

  // Policy Settings
  CSPM_SETTINGS_POLICIES: '/api/v1/cspm/settings/policies',

  // Scanning Settings
  CSPM_SETTINGS_SCANNING: '/api/v1/cspm/settings/scanning',

  // Notification Settings
  CSPM_SETTINGS_NOTIFICATIONS: '/api/v1/cspm/settings/notifications',

  // Settings Audit
  CSPM_SETTINGS_AUDIT: '/api/v1/cspm/settings/audit',

  // ==================== SECURITY GRAPH VISUALIZATION ====================
  // (security-graph service port 8003 — via api-gateway)
  GRAPH_VISUALIZATION: '/api/v1/graph/visualization',
  GRAPH_OVERVIEW: '/api/v1/graph',
  GRAPH_STATISTICS: '/api/v1/graph/statistics',
  GRAPH_REFRESH: '/api/v1/graph/refresh',
  GRAPH_ATTACK_PATHS: '/api/v1/attack-paths',
  GRAPH_CROWN_JEWELS: '/api/v1/graph/crown-jewels',

  // ==================== GRAPH QUERY LANGUAGE ====================
  // (risk-graph-engine port 8113 — already implemented)
  GRAPH_QUERY: '/api/v1/graph/query',
  GRAPH_QUERY_VALIDATE: '/api/v1/graph/query/validate',
  GRAPH_QUERY_SCHEMA: '/api/v1/graph/query/schema',
  GRAPH_QUERY_EXAMPLES: '/api/v1/graph/query/examples',
  GRAPH_QUERY_HISTORY: '/api/v1/graph/query/history',
  GRAPH_SAVED_QUERIES: '/api/v1/graph/saved-queries',
  GRAPH_SAVED_QUERY: (id: string) => `/api/v1/graph/saved-queries/${id}`,
  GRAPH_SAVED_QUERY_EXECUTE: (id: string) => `/api/v1/graph/saved-queries/${id}/execute`,

  // ==================== AUTOMATION RULES ====================
  // (settings-service port 8105 — already implemented)
  AUTOMATION_RULES: '/api/v1/automation/rules',
  AUTOMATION_RULE: (id: string) => `/api/v1/automation/rules/${id}`,
  AUTOMATION_RULE_TOGGLE: (id: string) => `/api/v1/automation/rules/${id}/toggle`,
  AUTOMATION_RULE_TEST: (id: string) => `/api/v1/automation/rules/${id}/test`,
  AUTOMATION_RULE_EVALUATE: '/api/v1/automation/rules/evaluate',
  AUTOMATION_EXECUTIONS: '/api/v1/automation/executions',
  AUTOMATION_STATS: '/api/v1/automation/stats',

  // ==================== SAVED QUERIES & VIEWS ====================
  // (settings-service port 8105 — already implemented)
  SAVED_QUERIES: '/api/v1/saved-queries',
  SAVED_QUERY: (id: string) => `/api/v1/saved-queries/${id}`,
  SAVED_QUERY_SHARE: (id: string) => `/api/v1/saved-queries/${id}/share`,
  SAVED_VIEWS: '/api/v1/saved-views',
  SAVED_VIEW: (id: string) => `/api/v1/saved-views/${id}`,

  // ==================== SLA FRAMEWORK ====================
  // (remediation-service port 8016 — already implemented)
  SLA_POLICIES: '/api/v1/cspm/remediation/sla/policies',
  SLA_POLICY: (id: string) => `/api/v1/cspm/remediation/sla/policies/${id}`,
  SLA_POLICY_ACTIVATE: (id: string) => `/api/v1/cspm/remediation/sla/policies/${id}/activate`,
  SLA_STATUS: '/api/v1/cspm/remediation/sla/status',
  SLA_STATUS_TASK: (taskId: string) => `/api/v1/cspm/remediation/sla/status/${taskId}`,
  SLA_EVALUATE: '/api/v1/cspm/remediation/sla/evaluate',
  SLA_BREACHES: '/api/v1/cspm/remediation/sla/breaches',
  SLA_DASHBOARD: '/api/v1/cspm/remediation/sla/dashboard',
  SLA_REPORT: '/api/v1/cspm/remediation/sla/report',
  SLA_PAUSE: (taskId: string) => `/api/v1/cspm/remediation/sla/pause/${taskId}`,
  SLA_RESUME: (taskId: string) => `/api/v1/cspm/remediation/sla/resume/${taskId}`,

  // ==================== PROJECTS / TEAM SCOPING ====================
  // (settings-service port 8105 — already implemented)
  PROJECTS: '/api/v1/projects',
  PROJECT: (id: string) => `/api/v1/projects/${id}`,
  PROJECT_STATS: (id: string) => `/api/v1/projects/${id}/stats`,
  PROJECT_MEMBERS: (id: string) => `/api/v1/projects/${id}/members`,
  PROJECT_MEMBER: (id: string, memberId: string) => `/api/v1/projects/${id}/members/${memberId}`,
  PROJECT_POLICIES: (id: string) => `/api/v1/projects/${id}/policies`,
  PROJECT_POLICY: (id: string, policyId: string) => `/api/v1/projects/${id}/policies/${policyId}`,

  // ==================== FINDINGS UNIFIED (Wiz-style) ====================
  // R162 (2026-05-18): audit verified all working paths firsthand against the
  // gateway. The `/api/v1/findings` prefix is rewritten by the gateway to
  // `/api/v1/cspm/findings` via startswith-prefix matching in main.py:772, so
  // the FE constants below land on real BE handlers for: LIST (findings_collection.py:35),
  // SUMMARY (stats.py:432), GROUPED (stats.py:372), EVIDENCE (findings_item.py:183),
  // EXPORT (risk_export.py:109). BULK_ACTION + STATS have explicit identity/
  // alias rewrites in path_rewrites.py:990,993. Only GRAPH + RESOLVE have no
  // BE handler at the cspm prefix; flagged aspirational below.
  FINDINGS: '/api/v1/findings',
  FINDINGS_SUMMARY: '/api/v1/findings/summary',
  FINDINGS_GROUPED: '/api/v1/findings/grouped',
  // ASPIRATIONAL — no /api/v1/cspm/findings/{id}/graph handler. Wire BE first.
  FINDING_GRAPH: (id: string) => `/api/v1/findings/${id}/graph`,
  // ASPIRATIONAL at cspm prefix — only ai-spm pipeline-security implements
  // PUT /api/v1/findings/{id}/resolve; for cspm a PATCH on the finding to
  // status='resolved' (findings_item.py:631 /status) is the working path.
  FINDING_RESOLVE: (id: string) => `/api/v1/findings/${id}/resolve`,
  FINDING_EVIDENCE: (id: string) => `/api/v1/findings/${id}/evidence`,
  FINDINGS_BULK_ACTION: '/api/v1/findings/bulk-action',
  FINDINGS_EXPORT: '/api/v1/findings/export',
  FINDINGS_STATS: '/api/v1/findings/stats',

  // ==================== FINDINGS ENHANCED ====================
  CSPM_FINDING_SNOOZE: (id: string) => `/api/v1/cspm/findings/${id}/snooze`,
  CSPM_FINDING_EVIDENCE: (id: string) => `/api/v1/cspm/findings/${id}/evidence`,
  CSPM_FINDING_AUDIT_TRAIL: (id: string) => `/api/v1/cspm/findings/${id}/audit-trail`,
  CSPM_FINDINGS_AUTO_ASSIGN_RULES: '/api/v1/cspm/findings/auto-assign/rules',
  CSPM_FINDINGS_AUTO_ASSIGN_RULE: (id: string) => `/api/v1/cspm/findings/auto-assign/rules/${id}`,
  CSPM_FINDINGS_AGE_REPORT: '/api/v1/cspm/findings/age-report',

  // ==================== RISK ENHANCED ====================
  CSPM_RISK_EPSS: '/api/v1/cspm/risk/epss',
  CSPM_RISK_EPSS_SCORE: (findingId: string) => `/api/v1/cspm/risk/epss/${findingId}`,
  CSPM_RISK_BUSINESS_CRITICALITY: '/api/v1/cspm/risk/business-criticality',
  CSPM_RISK_BUSINESS_CRITICALITY_RESOURCE: (resourceId: string) => `/api/v1/cspm/risk/business-criticality/${resourceId}`,
  CSPM_RISK_AGE_DECAY: '/api/v1/cspm/risk/age-decay',
  CSPM_RISK_ROI: '/api/v1/cspm/risk/roi',

  // ==================== DRIFT DETECTOR (port 8110) ====================
  CSPM_DRIFT_BASELINE: '/api/v1/cspm/drift/baseline',
  CSPM_DRIFT_BASELINE_UPDATE: '/api/v1/cspm/drift/baseline/update',
  CSPM_DRIFT_DETECT: '/api/v1/cspm/drift/detect',
  CSPM_DRIFT_RESULTS: '/api/v1/cspm/drift/results',
  CSPM_DRIFT_RESULT: (id: string) => `/api/v1/cspm/drift/results/${id}`,
  CSPM_DRIFT_ACKNOWLEDGE: (id: string) => `/api/v1/cspm/drift/acknowledge/${id}`,
  CSPM_DRIFT_HISTORY: (resourceId: string) => `/api/v1/cspm/drift/history/${resourceId}`,
  CSPM_DRIFT_SUMMARY: '/api/v1/cspm/drift/summary',
  CSPM_DRIFT_AUTO_REMEDIATION_CONFIG: '/api/v1/cspm/drift/auto-remediation/config',
  CSPM_DRIFT_REMEDIATE: (id: string) => `/api/v1/cspm/drift/remediate/${id}`,
  CSPM_DRIFT_AUTO_REMEDIATE: '/api/v1/cspm/drift/auto-remediate',
  CSPM_DRIFT_REMEDIATION_HISTORY: '/api/v1/cspm/drift/remediation/history',
  CSPM_DRIFT_REMEDIATION_ACTIONS: '/api/v1/cspm/drift/remediation/actions',

  // ==================== REALTIME CSPM (port 8114) ====================
  CSPM_REALTIME_EVENTS_INGEST: '/api/v1/cspm/realtime/events/ingest',
  CSPM_REALTIME_EVENTS_INGEST_BATCH: '/api/v1/cspm/realtime/events/ingest/batch',
  CSPM_REALTIME_EVENTS: '/api/v1/cspm/realtime/events',
  CSPM_REALTIME_EVENT: (id: string) => `/api/v1/cspm/realtime/events/${id}`,
  CSPM_REALTIME_EVENTS_STREAM: '/api/v1/cspm/realtime/events/stream',
  CSPM_REALTIME_SOURCES: '/api/v1/cspm/realtime/sources',
  CSPM_REALTIME_SOURCE: (id: string) => `/api/v1/cspm/realtime/sources/${id}`,
  CSPM_REALTIME_SOURCE_STATUS: (id: string) => `/api/v1/cspm/realtime/sources/${id}/status`,
  CSPM_REALTIME_RULES: '/api/v1/cspm/realtime/rules',
  CSPM_REALTIME_RULE: (id: string) => `/api/v1/cspm/realtime/rules/${id}`,
  CSPM_REALTIME_DETECTIONS: '/api/v1/cspm/realtime/detections',
  CSPM_REALTIME_DETECTION: (id: string) => `/api/v1/cspm/realtime/detections/${id}`,
  CSPM_REALTIME_DETECTION_STATUS: (id: string) => `/api/v1/cspm/realtime/detections/${id}/status`,
  CSPM_REALTIME_STATISTICS: '/api/v1/cspm/realtime/statistics',
  CSPM_REALTIME_DASHBOARD: '/api/v1/cspm/realtime/dashboard',

  // ==================== EXCEPTION SERVICE (port 8111) ====================
  CSPM_EXCEPTIONS: '/api/v1/cspm/exceptions',
  CSPM_EXCEPTION: (id: string) => `/api/v1/cspm/exceptions/${id}`,
  CSPM_EXCEPTIONS_SUMMARY: '/api/v1/cspm/exceptions/summary',
  CSPM_EXCEPTIONS_CHECK: '/api/v1/cspm/exceptions/check',
  CSPM_EXCEPTIONS_EXPIRED: '/api/v1/cspm/exceptions/expired',
  CSPM_EXCEPTION_APPROVE: (id: string) => `/api/v1/cspm/exceptions/${id}/approve`,
  CSPM_EXCEPTION_REJECT: (id: string) => `/api/v1/cspm/exceptions/${id}/reject`,
  CSPM_EXCEPTION_REVOKE: (id: string) => `/api/v1/cspm/exceptions/${id}/revoke`,
  CSPM_EXCEPTION_IMPACT: (id: string) => `/api/v1/cspm/exceptions/${id}/impact`,
  CSPM_EXCEPTIONS_BULK_APPROVE: '/api/v1/cspm/exceptions/bulk-approve',
  CSPM_EXCEPTIONS_BULK_REJECT: '/api/v1/cspm/exceptions/bulk-reject',
  CSPM_EXCEPTIONS_BULK_CREATE: '/api/v1/cspm/exceptions/bulk-create',
  CSPM_EXCEPTIONS_ANALYTICS: '/api/v1/cspm/exceptions/analytics',
  CSPM_EXCEPTIONS_SIMULATE_IMPACT: '/api/v1/cspm/exceptions/simulate-impact',
  CSPM_EXCEPTIONS_IMPACT_SUMMARY: '/api/v1/cspm/exceptions/impact-summary',
  CSPM_EXCEPTIONS_APPROVAL_RULES: '/api/v1/cspm/exceptions/approval-rules',
  CSPM_EXCEPTIONS_APPROVAL_RULE: (id: string) => `/api/v1/cspm/exceptions/approval-rules/${id}`,
  CSPM_EXCEPTIONS_APPROVAL_RULE_TOGGLE: (id: string) => `/api/v1/cspm/exceptions/approval-rules/${id}/toggle`,
  CSPM_EXCEPTIONS_APPROVAL_RULE_TEST: (id: string) => `/api/v1/cspm/exceptions/approval-rules/${id}/test`,
  CSPM_EXCEPTIONS_APPROVAL_RULES_APPLY: '/api/v1/cspm/exceptions/approval-rules/apply',

  // ==================== CI/CD SCANNER (port 8115) ====================
  CSPM_CICD_SCAN_IAC: '/api/v1/cspm/cicd/scan/iac',
  CSPM_CICD_SCAN_IMAGE: '/api/v1/cspm/cicd/scan/image',
  CSPM_CICD_SCAN_CODE: '/api/v1/cspm/cicd/scan/code',
  CSPM_CICD_SCAN_DIRECTORY: '/api/v1/cspm/cicd/scan/directory',
  CSPM_CICD_SCANS: '/api/v1/cspm/cicd/scans',
  CSPM_CICD_SCAN: (id: string) => `/api/v1/cspm/cicd/scans/${id}`,
  CSPM_CICD_SCAN_SARIF: (id: string) => `/api/v1/cspm/cicd/scans/${id}/sarif`,
  CSPM_CICD_POLICIES: '/api/v1/cspm/cicd/policies',
  CSPM_CICD_POLICY: (id: string) => `/api/v1/cspm/cicd/policies/${id}`,
  CSPM_CICD_EVALUATE: '/api/v1/cspm/cicd/evaluate',
  CSPM_CICD_INTEGRATIONS: '/api/v1/cspm/cicd/integrations',
  CSPM_CICD_INTEGRATION: (id: string) => `/api/v1/cspm/cicd/integrations/${id}`,
  CSPM_CICD_INTEGRATION_WEBHOOK: (id: string) => `/api/v1/cspm/cicd/integrations/${id}/webhook`,
  CSPM_CICD_WEBHOOK: (id: string) => `/api/v1/cspm/cicd/webhook/${id}`,
  CSPM_CICD_STATISTICS: '/api/v1/cspm/cicd/statistics',

  // ==================== SIEM EXPORT (port 8116) ====================
  CSPM_SIEM_CONNECTORS: '/api/v1/cspm/siem/connectors',
  CSPM_SIEM_CONNECTOR: (id: string) => `/api/v1/cspm/siem/connectors/${id}`,
  CSPM_SIEM_CONNECTOR_TEST: (id: string) => `/api/v1/cspm/siem/connectors/${id}/test`,
  CSPM_SIEM_CONNECTOR_SYNC: (id: string) => `/api/v1/cspm/siem/connectors/${id}/sync`,
  CSPM_SIEM_CONNECTOR_LOGS: (id: string) => `/api/v1/cspm/siem/connectors/${id}/logs`,
  CSPM_SIEM_EXPORT_FINDINGS: '/api/v1/cspm/siem/export/findings',
  CSPM_SIEM_EXPORT_EVENTS: '/api/v1/cspm/siem/export/events',
  CSPM_SIEM_EXPORT_COMPLIANCE: '/api/v1/cspm/siem/export/compliance',
  CSPM_SIEM_EXPORT_FORMATS: '/api/v1/cspm/siem/export/formats',
  CSPM_SIEM_WEBHOOKS: '/api/v1/cspm/siem/webhooks',
  CSPM_SIEM_WEBHOOK: (id: string) => `/api/v1/cspm/siem/webhooks/${id}`,
  CSPM_SIEM_STATISTICS: '/api/v1/cspm/siem/statistics',
  CSPM_SIEM_SCHEMA_OCSF: '/api/v1/cspm/siem/schema/ocsf',

  // ==================== ORG SCANNER (port 8112) ====================
  CSPM_ORG_ORGANIZATIONS: '/api/v1/cspm/org/organizations',
  CSPM_ORG_ORGANIZATION: (id: string) => `/api/v1/cspm/org/organizations/${id}`,
  CSPM_ORG_ANALYTICS: '/api/v1/cspm/org/organizations/analytics',
  CSPM_ORG_DISCOVER: (id: string) => `/api/v1/cspm/org/organizations/${id}/discover`,
  CSPM_ORG_ACCOUNTS: (id: string) => `/api/v1/cspm/org/organizations/${id}/accounts`,
  CSPM_ORG_HIERARCHY: (id: string) => `/api/v1/cspm/org/organizations/${id}/hierarchy`,
  CSPM_ORG_SCAN: (id: string) => `/api/v1/cspm/org/organizations/${id}/scan`,
  CSPM_ORG_SCAN_STATUS: (id: string, scanId: string) => `/api/v1/cspm/org/organizations/${id}/scan/${scanId}`,
  CSPM_ORG_COMPLIANCE: (id: string) => `/api/v1/cspm/org/organizations/${id}/compliance`,
  CSPM_ORG_FINDINGS: (id: string) => `/api/v1/cspm/org/organizations/${id}/findings`,
  CSPM_ORG_POSTURE: (id: string) => `/api/v1/cspm/org/organizations/${id}/posture`,
  CSPM_ORG_COMPLIANCE_REPORT: (id: string) => `/api/v1/cspm/org/organizations/${id}/compliance-report`,
  CSPM_ORG_POLICY_CONFLICTS: (id: string) => `/api/v1/cspm/org/organizations/${id}/policy-conflicts`,
  CSPM_ORG_HEALTH: (id: string) => `/api/v1/cspm/org/organizations/${id}/health`,
  CSPM_ORG_ONBOARD: (id: string) => `/api/v1/cspm/org/organizations/${id}/onboard`,

  // ── Agentic AI (Green / Blue / Red Agents) ────────────────────────────
  // Green Agent (Automated Remediation)
  GREEN_AGENT_STATUS: '/api/v1/green-agent/status',
  GREEN_AGENT_ANALYZE: '/api/v1/green-agent/analyze',
  GREEN_AGENT_EXECUTE: '/api/v1/green-agent/execute',
  GREEN_AGENT_AUTO_FIX: '/api/v1/green-agent/auto-fix',
  GREEN_AGENT_HISTORY: '/api/v1/green-agent/history',
  GREEN_AGENT_START: '/api/v1/green-agent/start',
  GREEN_AGENT_STOP: '/api/v1/green-agent/stop',

  // Red Agent (Offensive Security Testing)
  RED_AGENT_SIMULATE: '/api/v1/red-agent/simulate',
  RED_AGENT_ATTACK_PATHS: '/api/v1/red-agent/attack-paths',
  RED_AGENT_ATTACK_PATH: (id: string) => `/api/v1/red-agent/attack-paths/${id}`,
  RED_AGENT_API_TEST: '/api/v1/red-agent/api-test',
  RED_AGENT_IAM_AUDIT: '/api/v1/red-agent/iam-audit',
  RED_AGENT_REPORTS: '/api/v1/red-agent/reports',
  RED_AGENT_REPORT: (id: string) => `/api/v1/red-agent/reports/${id}`,
  RED_AGENT_FULL_ASSESSMENT: '/api/v1/red-agent/full-assessment',
  RED_AGENT_STATS: '/api/v1/red-agent/stats',

  // ── Data Warehouse Scanning (DSPM) ────────────────────────────────────
  WAREHOUSE_SCAN: '/api/v1/warehouse/scan',
  WAREHOUSE_CONNECTIONS: '/api/v1/warehouse/connections',
  WAREHOUSE_FINDINGS: '/api/v1/warehouse/findings',
  WAREHOUSE_COVERAGE: '/api/v1/warehouse/coverage',

  // ── Behavioral Baselines (CWPP Runtime) ───────────────────────────────
  BASELINES: '/api/v1/baselines',
  BASELINE: (id: string) => `/api/v1/baselines/${id}`,
  BASELINE_RESET: (id: string) => `/api/v1/baselines/${id}/reset`,
  BASELINE_ANOMALIES: '/api/v1/baselines/anomalies',
  BASELINE_EVENTS: '/api/v1/baselines/events',
  BASELINE_METRICS: '/api/v1/baselines/metrics',

  // ── SaaS Identity Connectors (CIEM) ───────────────────────────────────
  SAAS_IDENTITY_PROVIDERS: '/api/v1/saas-identities/providers',
  SAAS_IDENTITY_SCAN: (provider: string) => `/api/v1/saas-identities/scan/${provider}`,
  SAAS_IDENTITY_USERS: '/api/v1/saas-identities/users',
  SAAS_IDENTITY_RISKS: '/api/v1/saas-identities/risks',
  SAAS_IDENTITY_MFA: '/api/v1/saas-identities/mfa-coverage',
  SAAS_IDENTITY_CROSS_CLOUD: '/api/v1/saas-identities/cross-cloud',

  // ── SBOM Generation ───────────────────────────────────────────────────
  SBOM_GENERATE: '/api/v1/sbom/generate',
  SBOM_GENERATE_INFRA: '/api/v1/sbom/generate/infrastructure',
  SBOM_MERGE: '/api/v1/sbom/merge',

  // ── FAIR Risk Quantification ──────────────────────────────────────────
  RISK_QUANTIFY: '/api/v1/analytics/risk/quantify',
  RISK_QUANTIFY_BATCH: '/api/v1/analytics/risk/quantify/batch',
  RISK_PORTFOLIO: '/api/v1/analytics/risk/portfolio',
  RISK_REMEDIATION_ROI: '/api/v1/analytics/risk/remediation-roi',
};
